import { View, Text, Pressable, StyleSheet, ActivityIndicator, Linking, TextInput, Platform, useWindowDimensions, ScrollView } from 'react-native'
import { KeyboardAvoidingView } from 'react-native-keyboard-controller'
import { useState, useEffect } from 'react'
import { useRouter } from 'expo-router'
import { SafeAreaView } from 'react-native-safe-area-context'
import { Shield, ArrowRight, ArrowLeft, ChevronDown, ChevronUp, ExternalLink, Lock, Check, Star } from 'lucide-react-native'
import { useAuth } from '../../src/auth/AuthProvider'
import { colors, spacing, borderRadius, typography, shadows, fontFamily } from '../../src/theme'
import { events, getPostHog, registerAbBucket } from '../../src/lib/posthog'
import { toast } from '../../src/components/Toast'
import { API_URL } from '../../src/api/client'
import { storage, TOKEN_KEY, USER_ID_KEY, HAS_LOGGED_IN_KEY, ATTRIBUTION_CODE_KEY, USP_SELECTION_KEY, AB_BUCKET_KEY } from '../../src/utils/storage'
import axios from 'axios'

const AB_EXPERIMENT = 'what_matters_v1'

function assignBucket(): 'what_matters' | 'direct' {
  // Stable 50/50 split via last hex char of PostHog distinct_id
  const ph = getPostHog()
  const distinctId = ph?.getDistinctId?.() ?? Math.random().toString(16).slice(2)
  const lastChar = distinctId.slice(-1).toLowerCase()
  const n = parseInt(lastChar, 16)
  return isNaN(n) ? 'direct' : (n % 2 === 0 ? 'what_matters' : 'direct')
}

function isMetaInAppBrowser(): boolean {
  if (Platform.OS !== 'web') return false
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  return /FBAN|FBAV|Instagram|FB_IAB|FBIOS|FBANDROID/.test(ua)
}

function openInSystemBrowser() {
  const url = typeof window !== 'undefined' ? window.location.href : 'https://app.forbrugeragenten.dk/'
  if (/Android/.test(navigator.userAgent)) {
    window.location.href = `intent:${url}#Intent;package=com.android.chrome;scheme=https;end`
  } else {
    window.location.href = url.replace(/^https?:\/\//, 'x-safari-https://')
  }
}

export default function LoginScreen() {
  const { loginWithRedirect, isAuthenticated, isLoading: authLoading, refreshUser, user } = useAuth()
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [phone, setPhone] = useState('')
  const [reviewEnabled, setReviewEnabled] = useState(false)
  const [reviewExpanded, setReviewExpanded] = useState(false)
  const [reviewEmail, setReviewEmail] = useState('')
  const [reviewPassword, setReviewPassword] = useState('')
  const [reviewLoading, setReviewLoading] = useState(false)
  const [metaWebView] = useState(() => isMetaInAppBrowser())
  const [isReturning, setIsReturning] = useState(false)
  const router = useRouter()
  const { height: screenHeight } = useWindowDimensions()

  useEffect(() => {
    if (isAuthenticated && user) {
      if (!user.onboarding_completed) {
        router.replace('/onboarding')
      } else {
        router.replace('/(tabs)')
      }
    }
  }, [isAuthenticated, user?.onboarding_completed, router])

  useEffect(() => {
    storage.get(HAS_LOGGED_IN_KEY).then(val => {
      if (val === '1') setIsReturning(true)
    })
    // URL param override for preview (dev/testing only)
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search)
      if (params.get('returning') === '1') setIsReturning(true)
      if (params.get('returning') === '0') setIsReturning(false)

      // Capture tracking_code / utm_content for attribution (visitkort QR campaigns)
      // Persist to storage so it survives MitID redirect roundtrip.
      const code = params.get('tracking_code') || params.get('utm_content')
      if (code) {
        storage.set(ATTRIBUTION_CODE_KEY, code).catch(() => {})
      }
    }
    axios.get(`${API_URL}/api/v1/auth/review-mode`)
      .then(res => setReviewEnabled(res.data?.enabled === true))
      .catch(() => {})
  }, [])

  // A/B-bucket + USP-selector redirect-guard
  // Runs once auth state is known. New users without an existing USP selection
  // are assigned to a bucket; what_matters bucket redirects to /what-matters.
  useEffect(() => {
    if (authLoading || isAuthenticated) return
    let cancelled = false
    ;(async () => {
      const hasLoggedIn = await storage.get(HAS_LOGGED_IN_KEY)
      if (hasLoggedIn === '1') return // returning users bypass experiment
      const existing = await storage.get(USP_SELECTION_KEY)
      if (existing !== null) return // decision already made this session

      const cachedBucket = await storage.get(AB_BUCKET_KEY)
      const bucket = (cachedBucket === 'what_matters' || cachedBucket === 'direct')
        ? cachedBucket
        : assignBucket()
      if (!cachedBucket) {
        await storage.set(AB_BUCKET_KEY, bucket)
        events.abBucketAssigned(AB_EXPERIMENT, bucket)
      }
      registerAbBucket(AB_EXPERIMENT, bucket)

      if (!cancelled && bucket === 'what_matters') {
        router.replace('/what-matters' as any)
      }
    })()
    return () => { cancelled = true }
  }, [authLoading, isAuthenticated, router])

  const handleLogin = async () => {
    // Read attribution code early — needed for both pre-register AND loginWithRedirect
    const attributionCode = await storage.get(ATTRIBUTION_CODE_KEY).catch(() => null)

    // Read USP selection from /what-matters (variant bucket) — undefined for control/skip
    let uspSelection: string[] | undefined
    try {
      const raw = await storage.get(USP_SELECTION_KEY)
      if (raw !== null) uspSelection = JSON.parse(raw) as string[]
    } catch {
      // Corrupt value — treat as no selection
    }

    // Pre-register phone for lead capture (before MitID)
    let leadId: string | undefined
    if (phone.trim().length >= 8 && !isReturning) {
      try {
        const preRes = await axios.post(`${API_URL}/api/v1/auth/pre-register`, {
          phone: phone.trim(),
          platform: Platform.OS,
          ...(attributionCode
            ? {
                utm_source: 'visitkort',
                utm_medium: 'qr',
                utm_content: attributionCode,
              }
            : {}),
          ...(uspSelection !== undefined ? { usp_selection: uspSelection } : {}),
        })
        leadId = preRes.data?.lead_id
        events.phoneEntered(Platform.OS, uspSelection?.length ?? 0)
      } catch {
        // Non-blocking
      }
    }

    try {
      setIsRedirecting(true)
      events.loginStarted()
      await loginWithRedirect(leadId, attributionCode || undefined)
      if (__DEV__ || process.env.EXPO_PUBLIC_DEV_MODE === 'true') {
        router.replace('/(tabs)')
      }
    } catch (error) {
      console.error('Login error:', error)
      toast.error('Kunne ikke starte MitID login. Prøv igen.')
      setIsRedirecting(false)
    }
  }

  const handleReviewLogin = async () => {
    if (!reviewEmail || !reviewPassword) {
      toast.error('Indtast email og adgangskode')
      return
    }
    try {
      setReviewLoading(true)
      const res = await axios.post(`${API_URL}/api/v1/auth/review-login`, {
        email: reviewEmail,
        password: reviewPassword,
      })
      const { access_token, user_id } = res.data
      await storage.set(TOKEN_KEY, access_token)
      await storage.set(USER_ID_KEY, user_id)
      await refreshUser()
      router.replace('/(tabs)')
    } catch (error: any) {
      console.error('Review login error:', error)
      toast.error(error?.response?.data?.detail || 'Login fejlede')
      setReviewLoading(false)
    }
  }

  const loginButton = (
    <Pressable
      style={[styles.loginButton, (authLoading || isRedirecting || metaWebView) && styles.loginButtonDisabled]}
      onPress={handleLogin}
      disabled={authLoading || isRedirecting || metaWebView}
    >
      {authLoading || isRedirecting ? (
        <View style={styles.loginButtonInner}>
          <ActivityIndicator color={colors.text.inverse} size="small" />
          <Text style={styles.loginButtonText}>
            {isRedirecting ? 'Starter MitID...' : 'Indlæser...'}
          </Text>
        </View>
      ) : (
        <View style={styles.loginButtonInner}>
          <Shield size={20} color={colors.text.inverse} />
          <Text style={styles.loginButtonText}>{isReturning ? 'Log ind' : 'Log ind med MitID'}</Text>
          <ArrowRight size={20} color={colors.text.inverse} />
        </View>
      )}
    </Pressable>
  )

  const metaBannerEl = metaWebView ? (
    <View style={styles.metaBanner}>
      <Text style={styles.metaBannerTitle}>Åbn i din browser for at logge ind</Text>
      <Text style={styles.metaBannerBody}>
        MitID virker ikke i Facebook eller Instagram. Tryk nedenfor for at åbne i Safari eller Chrome.
      </Text>
      <Pressable style={styles.metaBannerButton} onPress={openInSystemBrowser}>
        <ExternalLink size={18} color="#FFFFFF" />
        <Text style={styles.metaBannerButtonText}>Åbn i Safari / Chrome</Text>
      </Pressable>
    </View>
  ) : null

  const reviewLoginEl = reviewEnabled ? (
    <View style={{ marginTop: spacing.xs }}>
      <Pressable
        style={styles.reviewToggle}
        onPress={() => setReviewExpanded(!reviewExpanded)}
      >
        <Text style={styles.reviewToggleText}>App Store Review</Text>
        {reviewExpanded
          ? <ChevronUp size={16} color={colors.text.muted} />
          : <ChevronDown size={16} color={colors.text.muted} />
        }
      </Pressable>
      {reviewExpanded && (
        <View style={styles.reviewForm}>
          <TextInput
            style={styles.reviewInput}
            placeholder="Email"
            value={reviewEmail}
            onChangeText={setReviewEmail}
            autoCapitalize="none"
            keyboardType="email-address"
            autoComplete="email"
          />
          <TextInput
            style={styles.reviewInput}
            placeholder="Adgangskode"
            value={reviewPassword}
            onChangeText={setReviewPassword}
            secureTextEntry
            autoComplete="password"
          />
          <Pressable
            style={[styles.reviewButton, reviewLoading && styles.loginButtonDisabled]}
            onPress={handleReviewLogin}
            disabled={reviewLoading}
          >
            {reviewLoading ? (
              <ActivityIndicator color={colors.text.inverse} size="small" />
            ) : (
              <Text style={styles.reviewButtonText}>Log ind</Text>
            )}
          </Pressable>
        </View>
      )}
    </View>
  ) : null

  const termsEl = (
    <Text style={styles.disclaimer}>
      Ved at logge ind accepterer du vores{' '}
      <Text style={styles.termsLink} onPress={() => Linking.openURL('https://forbrugeragenten.dk/betingelser')}>
        vilkår
      </Text>
      {' '}og{' '}
      <Text style={styles.termsLink} onPress={() => Linking.openURL('https://forbrugeragenten.dk/privatlivspolitik')}>
        privatlivspolitik
      </Text>
    </Text>
  )

  // ── Returning user: compact login ──
  if (isReturning) {
    return (
      <SafeAreaView style={styles.container}>
        <Pressable
          style={styles.backButton}
          onPress={() => Linking.openURL('https://forbrugeragenten.dk')}
          hitSlop={8}
        >
          <ArrowLeft size={18} color={colors.text.secondary} />
          <Text style={styles.backButtonText}>Hjem</Text>
        </Pressable>

        <ScrollView
          contentContainerStyle={styles.centerContent}
          showsVerticalScrollIndicator={false}
          bounces={false}
          keyboardShouldPersistTaps="handled"
        >
          <View style={styles.logoContainer}>
            <View style={[styles.logoCircle, { marginBottom: 16 }]}>
              <Shield size={32} color="#FFFFFF" strokeWidth={2.25} />
            </View>
            <Text style={styles.returningAppName}>ForbrugerAgenten</Text>
            <Text style={[styles.title, { marginTop: spacing.xl }]}>Velkommen tilbage</Text>
            <Text style={[styles.subtitle, { marginTop: 6 }]}>Log ind for at se hvad din agent har fundet</Text>
          </View>

          {metaBannerEl}

          <View style={[{ marginTop: spacing.xxl }, metaWebView && styles.cardDisabled]}>
            {loginButton}
          </View>

          <View style={{ marginTop: spacing.xl }}>
            {reviewLoginEl}
            {termsEl}
          </View>
        </ScrollView>
      </SafeAreaView>
    )
  }

  // ── New user: agent-identity first screen ──
  const s = Math.min(1, Math.max(0, (screenHeight - 667) / (932 - 667)))
  const lerp = (min: number, max: number) => Math.round(min + (max - min) * s)

  return (
    <SafeAreaView style={styles.container}>
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined} keyboardVerticalOffset={Platform.OS === 'ios' ? 90 : 0}>
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        bounces={false}
        keyboardShouldPersistTaps="handled"
      >
        {/* Logo — brand anchor, scales with screen */}
        <View style={[styles.logoContainer, { marginBottom: lerp(20, 28) }]}>
          <View style={[styles.logoCircle, { width: lerp(48, 62), height: lerp(48, 62), borderRadius: lerp(14, 18) }]}>
            <Shield size={lerp(24, 32)} color="#FFFFFF" />
          </View>
          <Text style={[styles.appName, { fontSize: lerp(13, 15) }]}>ForbrugerAgenten</Text>
        </View>

        {/* Hero — hook + agent identity */}
        <View style={[styles.heroSection, { marginBottom: lerp(20, 28) }]}>
          <Text style={[styles.heroTitle, { fontSize: lerp(26, 34) }]}>Spar 4.200 kr/år{'\n'}på dine udbydere</Text>
          <Text style={[styles.heroSubtext, { fontSize: lerp(16, 18), marginTop: lerp(12, 16), maxWidth: 340 }]}>
            Vi taler med dine udbydere, overvåger dine regninger og sørger for du aldrig betaler for meget
          </Text>
        </View>

        {/* Social proof badge — bridges hero and action zones */}
        <View style={[styles.proofBadge, { marginBottom: lerp(18, 24) }]}>
          <Star size={14} color={colors.primary} fill={colors.primary} />
          <Text style={styles.proofBadgeText} numberOfLines={1}>5.0 i App Store · 79+ udbydere</Text>
        </View>

        {metaBannerEl}

        {/* Action zone — phone + CTA grouped tightly */}
        <View style={[{ maxWidth: 440, width: '100%', alignSelf: 'center' }, metaWebView && styles.cardDisabled]}>
          <View style={[styles.phoneInputRow, { marginBottom: lerp(10, 12) }]}>
            <Text style={styles.phonePrefix}>+45</Text>
            <TextInput
              style={[styles.phoneInput, { paddingVertical: lerp(12, 14) }]}
              value={phone}
              onChangeText={(t) => setPhone(t.replace(/\D/g, ''))}
              placeholder="12345678"
              placeholderTextColor={colors.text.muted}
              keyboardType="phone-pad"
              autoComplete="tel"
              maxLength={8}
            />
          </View>

          {loginButton}
        </View>

        {/* Trust + facts — single compact zone */}
        <Text style={[styles.pillsLine, { marginTop: lerp(14, 20) }]}>Dit nummer deles aldrig · 100% gratis · Ingen binding</Text>

        {reviewLoginEl}
        {termsEl}
      </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.surface,
  },
  backButton: {
    position: 'absolute',
    top: 16,
    left: spacing.lg,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    zIndex: 10,
    paddingVertical: 6,
    paddingHorizontal: 4,
  },
  backButtonText: {
    ...typography.small,
    color: colors.text.secondary,
  },
  centerContent: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingHorizontal: spacing.md,
    maxWidth: 440,
    alignSelf: 'center',
    width: '100%',
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.lg,
    maxWidth: 440,
    alignSelf: 'center',
    width: '100%',
  },
  logoContainer: {
    alignItems: 'center',
  },
  appName: {
    fontFamily: fontFamily.semiBold,
    fontWeight: '600',
    fontSize: 15,
    color: colors.text.secondary,
    marginTop: 6,
  },
  proofBadge: {
    backgroundColor: colors.primaryLight,
    paddingVertical: 10,
    paddingHorizontal: 18,
    borderRadius: 22,
    alignSelf: 'center',
    borderWidth: 1,
    borderColor: colors.primary + '33',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  proofBadgeText: {
    fontFamily: fontFamily.bold,
    fontWeight: '700',
    fontSize: 14,
    color: colors.primary,
    textAlign: 'center',
    letterSpacing: 0.1,
  },
  logoCircle: {
    width: 52,
    height: 52,
    borderRadius: 16,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 6,
  },
  title: {
    fontSize: 30,
    fontWeight: '700',
    fontFamily: fontFamily.bold,
    color: colors.text.primary,
    textAlign: 'center',
    letterSpacing: -0.4,
    lineHeight: 36,
  },
  subtitle: {
    fontSize: 16,
    fontFamily: fontFamily.medium,
    fontWeight: '500',
    color: colors.text.secondary,
    textAlign: 'center',
    marginTop: 2,
    lineHeight: 22,
    maxWidth: 320,
  },
  returningAppName: {
    fontFamily: fontFamily.semiBold,
    fontWeight: '600',
    fontSize: 14,
    color: colors.text.secondary,
    letterSpacing: 0.3,
  },
  // (mainCard removed — simplified first screen)
  // Hero
  heroSection: {
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  heroTitle: {
    fontSize: 34,
    fontWeight: '700',
    fontFamily: fontFamily.bold,
    color: colors.text.primary,
    textAlign: 'center',
    lineHeight: 40,
    letterSpacing: -0.3,
  },
  heroSubtext: {
    fontSize: 17,
    fontFamily: fontFamily.medium,
    fontWeight: '500',
    color: colors.text.secondary,
    textAlign: 'center',
    lineHeight: 25,
  },
  // Pills line (under card)
  pillsLine: {
    fontFamily: fontFamily.regular,
    fontSize: 13,
    color: colors.text.muted,
    textAlign: 'center',
    marginTop: spacing.md,
  },
  // Phone input
  phoneInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: borderRadius.md,
    overflow: 'hidden',
  },
  phonePrefix: {
    fontFamily: fontFamily.semiBold,
    fontWeight: '600',
    fontSize: 16,
    color: colors.text.secondary,
    paddingHorizontal: 14,
    paddingVertical: 14,
    borderRightWidth: 1,
    borderRightColor: colors.border,
  },
  phoneInput: {
    flex: 1,
    fontFamily: fontFamily.regular,
    fontSize: 16,
    color: colors.text.primary,
    paddingHorizontal: 14,
    paddingVertical: 14,
  },
  // Login button
  loginButton: {
    backgroundColor: colors.primary,
    paddingVertical: 16,
    borderRadius: borderRadius.lg,
    alignItems: 'center',
    minHeight: 54,
    justifyContent: 'center',
    ...shadows.colored(colors.primary),
  },
  loginButtonInner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  loginButtonDisabled: {
    opacity: 0.5,
  },
  loginButtonText: {
    ...typography.bodyBold,
    color: colors.text.inverse,
    fontSize: 18,
  },
  // Trust
  trustRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    marginTop: spacing.sm,
  },
  trustText: {
    fontFamily: fontFamily.regular,
    fontSize: 12,
    color: colors.text.muted,
  },
  trustBadgeText: {
    fontFamily: fontFamily.semiBold,
    fontWeight: '600',
    fontSize: 14,
    color: colors.primary,
  },
  cardDisabled: {
    opacity: 0.5,
  },
  disclaimer: {
    fontFamily: fontFamily.regular,
    color: colors.text.muted,
    textAlign: 'center',
    marginTop: spacing.sm,
    fontSize: 11,
  },
  termsLink: {
    color: colors.primary,
  },
  // Meta banner
  metaBanner: {
    backgroundColor: '#FFF3CD',
    borderRadius: borderRadius.lg,
    padding: spacing.md,
    borderWidth: 1,
    borderColor: '#F59E0B',
    marginBottom: spacing.md,
    gap: spacing.sm,
  },
  metaBannerTitle: {
    ...typography.bodyBold,
    color: '#92400E',
  },
  metaBannerBody: {
    ...typography.caption,
    color: '#78350F',
    lineHeight: 20,
  },
  metaBannerButton: {
    backgroundColor: colors.primary,
    paddingVertical: 12,
    paddingHorizontal: spacing.md,
    borderRadius: borderRadius.md,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    minHeight: 44,
    marginTop: spacing.xs,
  },
  metaBannerButtonText: {
    ...typography.bodyBold,
    color: '#FFFFFF',
    fontSize: 14,
  },
  // Review login
  reviewToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingVertical: spacing.sm,
  },
  reviewToggleText: {
    ...typography.small,
    color: colors.text.muted,
  },
  reviewForm: {
    backgroundColor: colors.background,
    borderRadius: 12,
    padding: spacing.md,
    borderWidth: 1,
    borderColor: colors.border,
    marginTop: spacing.xs,
  },
  reviewInput: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: borderRadius.md,
    paddingHorizontal: spacing.md,
    paddingVertical: 12,
    ...typography.body,
    color: colors.text.primary,
    backgroundColor: colors.surface,
    marginBottom: spacing.sm,
  },
  reviewButton: {
    backgroundColor: colors.text.primary,
    paddingVertical: 12,
    borderRadius: borderRadius.md,
    alignItems: 'center',
    minHeight: 44,
    justifyContent: 'center',
    marginTop: spacing.xs,
  },
  reviewButtonText: {
    ...typography.bodyBold,
    color: colors.text.inverse,
    fontSize: 14,
  },
})
